import { skipToken, useMutation } from '@apollo/client'
import { zodResolver } from '@hookform/resolvers/zod'
import { Loader, Rocket } from 'lucide-react'
import { Suspense, useTransition } from 'react'
import { use } from 'react'
import { useForm } from 'react-hook-form'
import { useParams } from 'react-router'
import { toast } from 'sonner'
import { z } from 'zod'
import { CompanySuspensionContext } from '@/components/company-suspension/context'
import { LocationSelector } from '@/components/form/location-selector'
import { ToasterContent } from '@/components/sv/toaster-content'
import { Button } from '@/components/ui/button'
import { Form } from '@/components/ui/form'
import {
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { gql } from '@/lib/graphql/gql'
import { useIdCompany, useSuspenseQuery } from '@/lib/hooks'
import { DeployTemplateSheetFallback } from '@/routes/templates/deploy-template-sheet/fallback'
import type {
  DeployTemplateDataQuery,
  DeployTemplateDataQueryVariables,
  DeployTemplateMutation,
  DeployTemplateMutationVariables,
} from '@/routes/templates/deploy-template-sheet/index.types.gen'

const DEPLOY_TEMPLATE_MUTATION = gql`
  mutation DeployTemplate(
    $idCompany: String!
    $idCluster: String!
    $idTemplate: String!
  ) {
    deployTemplate(
      idCompany: $idCompany
      idCluster: $idCluster
      idTemplate: $idTemplate
    )
  }
`

const TEMPLATE_QUERY = gql`
  query DeployTemplateData($name: String!) {
    templateByName(name: $name) {
      id
      displayName
      categorySlug
    }
  }
`

type Props = {
  isOpen: boolean
  onClose: () => void
}

const schema = z.object({
  idCluster: z.string(),
})
type FormValues = z.input<typeof schema>

const Content = ({ isOpen, onClose }: Props) => {
  const { name = '' } = useParams()
  const { idCompany } = useIdCompany()
  const { isSuspended } = use(CompanySuspensionContext)
  const [isPending, startTransition] = useTransition()
  const { data } = useSuspenseQuery<
    DeployTemplateDataQuery,
    DeployTemplateDataQueryVariables
  >(TEMPLATE_QUERY, isOpen ? { variables: { name } } : skipToken)
  const template = data?.templateByName

  const [deployTemplate] = useMutation<
    DeployTemplateMutation,
    DeployTemplateMutationVariables
  >(DEPLOY_TEMPLATE_MUTATION)

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      idCluster: '',
    },
  })

  const onSubmit = async (values: FormValues) => {
    if (isPending) {
      return
    }
    startTransition(async () => {
      try {
        deployTemplate({
          variables: {
            idCompany,
            idCluster: values.idCluster,
            idTemplate: template?.id ?? '',
          },
        })
        toast(
          <ToasterContent variant="success">
            Template is being deployed
          </ToasterContent>,
          { position: 'top-right' }
        )
        onClose()
      } catch {
        toast(
          <ToasterContent variant="error">
            Failed to deploy template
          </ToasterContent>,
          { position: 'top-right' }
        )
      }
    })
  }

  return (
    <>
      <SheetHeader>
        <SheetTitle>Deploy {template?.displayName} template</SheetTitle>
      </SheetHeader>
      <div className="px-4 overflow-y-auto w-full pb-2">
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="grid gap-6">
            <LocationSelector />
          </form>
        </Form>
      </div>
      <SheetFooter className="sticky">
        <Button
          disabled={isPending || isSuspended}
          onClick={form.handleSubmit(onSubmit)}
        >
          {isPending ? <Loader className="animate-spin" /> : <Rocket />}
          Deploy template
        </Button>
      </SheetFooter>
    </>
  )
}

export const DeployTemplateSheet = ({ isOpen, onClose }: Props) => {
  return (
    <Sheet open={isOpen} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <SheetContent className="sm:max-w-[600px]">
        <Suspense fallback={<DeployTemplateSheetFallback />}>
          <Content isOpen={isOpen} onClose={onClose} />
        </Suspense>
      </SheetContent>
    </Sheet>
  )
}
